import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { View, Text, StyleSheet } from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { doc, updateDoc } from "firebase/firestore";
import { DndContext } from "@dnd-kit/core";

import AddBlockView from "../../../Block/AddBlockView";
import NetworkManager from "../../../Classes/NetworkManager";
import CopyBlockToClient from "../../../Block/CopyBlockToClient";
import DeleteBlockView from "../../../Block/DeleteBlockView";
import IconEmptyView from "../../../Components/IconEmptyView";
import { useScreenDimensions } from "../../../_layout";
import { useToast } from "../../../contexts/ToastContext";
import db from "../../../Firestore/FirestoreConfig";
import Colors from "../../../../Resources/Color";
import BrowserToolbar from "./BrowserToolbar";
import FolderSection from "./FolderSection";
import {
  buildFolderSections,
  createExpandedFoldersState,
  seedExpandedFolders,
  toggleExpandedFolder,
} from "./blocksListUtils";
import BlockSummaryModal from "./BlockSummaryModal";
import useBlockFolderDragKit from "./useBlockFolderDragKit";
import {
  applyOptimisticBlockLabel,
  buildBlockFolderMoveWritePlan,
  getFolderTargetFromOver,
  getPersistedFolderLabelForSection,
  isCrossFolderMove,
} from "./blockFolderDragUtils";
import {
  buildFolderRenameWritePlan,
  isFolderRenameUnchanged,
  resolveFolderRenameTargetLabel,
  toTrimmedLabel,
} from "./folderRenameUtils";
import { buildBlockRealtimeWritePayload } from "./blockRealtimeWriteUtils";

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
  },
  contentSurface: {
    flex: 1,
    minHeight: 400,
    width: "100%",
  },
  foldersContainer: {
    gap: 4,
    marginTop: 4,
  },
  offlineNoticeText: {
    color: Colors.grayColor,
    fontSize: 11,
    marginBottom: 8,
    opacity: 0.85,
  },
});

const BlocksList = observer(({ client, showRightIcons = true }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { showToast } = useToast();

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFolders, setExpandedFolders] = useState(createExpandedFoldersState);
  const [editingFolderKey, setEditingFolderKey] = useState(null);
  const [editingFolderLabelDraft, setEditingFolderLabelDraft] = useState("");
  const [editingFolderOriginalDisplayLabel, setEditingFolderOriginalDisplayLabel] =
    useState("");
  const [
    editingFolderOriginalPersistedLabel,
    setEditingFolderOriginalPersistedLabel,
  ] = useState("");
  const [isSavingFolderName, setIsSavingFolderName] = useState(false);
  const [showingAddBlock, setShowingAddBlock] = useState(false);
  const [preselectedFolderLabel, setPreselectedFolderLabel] = useState("");
  const [showingCopyToClient, setShowingCopyToClient] = useState(false);
  const [showingDeleteAlert, setShowingDeleteAlert] = useState(false);
  const [actionBlock, setActionBlock] = useState(null);
  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const [summaryBlockId, setSummaryBlockId] = useState(null);
  const [activeDragBlockId, setActiveDragBlockId] = useState(null);
  const [activeDragSourceFolderKey, setActiveDragSourceFolderKey] =
    useState(null);
  const [activeDragOverFolderKey, setActiveDragOverFolderKey] = useState(null);
  const [pressSuppressedBlockId, setPressSuppressedBlockId] = useState(null);
  const pressSuppressTimeoutRef = useRef(null);
  const { sensors } = useBlockFolderDragKit();

  const folderSections = buildFolderSections(client?.blocks, { searchTerm, t });
  const activeBlockId = client?.activeBlock?.()?.id ?? null;
  const isDragDisabledBySearch = searchTerm.trim() !== "";
  const isBlockDragDisabled =
    isDragDisabledBySearch || isSavingFolderName || Boolean(editingFolderKey);

  useEffect(() => {
    setExpandedFolders((prev) => seedExpandedFolders(prev, folderSections));
  }, [folderSections]);

  useEffect(
    () => () => {
      if (pressSuppressTimeoutRef.current) {
        clearTimeout(pressSuppressTimeoutRef.current);
        pressSuppressTimeoutRef.current = null;
      }
    },
    [],
  );

  const blockMenuItems = useMemo(
    () => [
      {
        key: "editBlock",
        label: t("editBlock-string"),
        icon: { type: "ionicons", name: "create-outline" },
      },
      {
        key: "copyBlock",
        label: t("copyBlock-string"),
        icon: { type: "ionicons", name: "duplicate-outline" },
      },
      {
        key: "copyFullBlock",
        label: t("copyFullBlock-string"),
        icon: { type: "ionicons", name: "duplicate-outline" },
      },
      {
        key: "copyToClient",
        label: t("copyToAnotherClient-string"),
        icon: { type: "ionicons", name: "person-add-outline" },
      },
      {
        key: "exportToLibrary",
        label: t("exportToLibrary-string"),
        icon: { type: "ionicons", name: "book-outline" },
      },
      {
        key: "delete",
        label: t("delete-string"),
        icon: { type: "ionicons", name: "trash-outline" },
        isDanger: true,
      },
    ],
    [t]
  );

  const handleOpenBlock = useCallback((block) => {
    block?.client?.setSelectedBlock?.(block);
  }, []);

  const handleSummaryPress = useCallback((block) => {
    if (!block?.id) return;
    setSummaryBlockId(block.id);
    setIsSummaryModalVisible(true);
  }, []);

  const handleToggleFolder = useCallback((folderKey) => {
    setExpandedFolders((prev) => toggleExpandedFolder(prev, folderKey));
  }, []);

  const handleEditFolderName = useCallback((section) => {
    if (!section || isSavingFolderName) return;

    const folderDisplayLabel = toTrimmedLabel(section.folderLabel);
    const firstBlockWithLabel = Array.isArray(section.blocks)
      ? section.blocks.find((block) => typeof block?.label === "string")
      : null;
    const persistedFolderLabel = toTrimmedLabel(firstBlockWithLabel?.label);

    setEditingFolderKey(section.folderKey);
    setEditingFolderLabelDraft(folderDisplayLabel);
    setEditingFolderOriginalDisplayLabel(folderDisplayLabel);
    setEditingFolderOriginalPersistedLabel(persistedFolderLabel);
  }, [isSavingFolderName]);

  const handleCancelFolderNameEdit = useCallback(() => {
    if (isSavingFolderName) return;
    setEditingFolderKey(null);
    setEditingFolderLabelDraft("");
    setEditingFolderOriginalDisplayLabel("");
    setEditingFolderOriginalPersistedLabel("");
  }, [isSavingFolderName]);

  const editingFolderSection = useMemo(
    () =>
      folderSections.find((section) => section.folderKey === editingFolderKey) ??
      null,
    [folderSections, editingFolderKey],
  );

  useEffect(() => {
    if (!editingFolderKey || editingFolderSection || isSavingFolderName) return;
    handleCancelFolderNameEdit();
  }, [
    editingFolderKey,
    editingFolderSection,
    isSavingFolderName,
    handleCancelFolderNameEdit,
  ]);

  const handleOpenAddBlockFromFolder = useCallback((section) => {
    const firstBlockWithLabel = Array.isArray(section?.blocks)
      ? section.blocks.find((block) => typeof block?.label === "string")
      : null;
    const persistedFolderLabel = toTrimmedLabel(firstBlockWithLabel?.label);

    setPreselectedFolderLabel(persistedFolderLabel);
    setShowingAddBlock(true);
  }, []);

  const handleCloseAddBlock = useCallback(() => {
    setShowingAddBlock(false);
    setPreselectedFolderLabel("");
  }, []);

  const handleSaveFolderName = useCallback(
    async () => {
      if (!editingFolderSection || !client || !client?.id || isSavingFolderName) {
        return;
      }

      if (
        isFolderRenameUnchanged(
          editingFolderLabelDraft,
          editingFolderOriginalDisplayLabel,
        )
      ) {
        handleCancelFolderNameEdit();
        return;
      }

      const targetLabel = resolveFolderRenameTargetLabel(
        editingFolderLabelDraft,
        editingFolderOriginalPersistedLabel,
      );
      const writePlan = buildFolderRenameWritePlan({
        client,
        blocks: editingFolderSection.blocks,
        targetLabel,
      });

      if (writePlan.length === 0) {
        handleCancelFolderNameEdit();
        return;
      }

      setIsSavingFolderName(true);
      try {
        const timestamp = NetworkManager.getInstance().getNetworkTime();
        const sessionId = client?.coach?.sessionId;
        const writePayload = buildBlockRealtimeWritePayload({
          label: targetLabel,
          sessionId,
          timestamp,
        });

        const previousAvoidTransaction = client.avoidTransaction;
        if (typeof client.setProperty === "function") {
          client.setProperty("avoidTransaction", true);
        } else {
          client.avoidTransaction = true;
        }

        try {
          editingFolderSection.blocks.forEach((block) => {
            block?.setProperty?.("label", targetLabel);
          });
        } finally {
          if (typeof client.setProperty === "function") {
            client.setProperty("avoidTransaction", previousAvoidTransaction);
          } else {
            client.avoidTransaction = previousAvoidTransaction;
          }
        }

        const results = await Promise.allSettled(
          writePlan.map((entry) =>
            updateDoc(
              doc(db, ...entry.blockDocPathSegments),
              writePayload,
            ),
          ),
        );

        if (results.some((result) => result.status === "rejected")) {
          showToast("folderRenameError-string", {
            type: "error",
            title: "error-string",
            description: "folderRenameError-string",
          });
        }
      } finally {
        setIsSavingFolderName(false);
        handleCancelFolderNameEdit();
      }
    },
    [
      editingFolderSection,
      client,
      editingFolderLabelDraft,
      editingFolderOriginalDisplayLabel,
      editingFolderOriginalPersistedLabel,
      isSavingFolderName,
      showToast,
      handleCancelFolderNameEdit,
    ],
  );

  const handleMenuAction = useCallback(
    (block, itemKey) => {
      if (!block) return;

      if (itemKey === "editBlock") {
        block.client.setSelectedBlock(block);
        block.setProperty("isShowingDashboard", true);
        return;
      }

      if (itemKey === "copyBlock" || itemKey === "copyFullBlock") {
        const newBlock = block.client.copyBlock(
          t,
          block,
          itemKey === "copyFullBlock",
          undefined,
          showToast
        );
        if (newBlock?.id) {
          block.client.updateClientTransactionData(newBlock.id);
          block.client.startOrResetBatchTimer();
        }
        return;
      }

      if (itemKey === "copyToClient") {
        setActionBlock(block);
        setShowingCopyToClient(true);
        return;
      }

      if (itemKey === "exportToLibrary") {
        block.copyBlockToLibrary(showToast);
        return;
      }

      if (itemKey === "delete") {
        setActionBlock(block);
        setShowingDeleteAlert(true);
      }
    },
    [showToast, t]
  );

  const handleDeleteBlock = useCallback(() => {
    if (!actionBlock) return;
    actionBlock.client.deleteBlock(actionBlock);
    setShowingDeleteAlert(false);
    setActionBlock(null);
  }, [actionBlock]);

  const handleCloseDeleteAlert = useCallback(() => {
    setShowingDeleteAlert(false);
    setActionBlock(null);
  }, []);

  const handleCloseCopyToClient = useCallback(() => {
    setShowingCopyToClient(false);
    setActionBlock(null);
  }, []);

  const handleCloseSummaryModal = useCallback(() => {
    setIsSummaryModalVisible(false);
  }, []);

  const schedulePressSuppressionClear = useCallback(() => {
    if (pressSuppressTimeoutRef.current) {
      clearTimeout(pressSuppressTimeoutRef.current);
    }

    pressSuppressTimeoutRef.current = setTimeout(() => {
      setPressSuppressedBlockId(null);
      pressSuppressTimeoutRef.current = null;
    }, 220);
  }, []);

  const clearDragState = useCallback(() => {
    setActiveDragBlockId(null);
    setActiveDragSourceFolderKey(null);
    setActiveDragOverFolderKey(null);
  }, []);

  const handleBlockDragStart = useCallback(
    (event) => {
      if (isBlockDragDisabled) return;

      const activeData = event?.active?.data?.current;
      if (!activeData || activeData.type !== "blockCard") return;

      if (pressSuppressTimeoutRef.current) {
        clearTimeout(pressSuppressTimeoutRef.current);
        pressSuppressTimeoutRef.current = null;
      }

      setActiveDragBlockId(activeData.blockId ?? null);
      setActiveDragSourceFolderKey(activeData.sourceFolderKey ?? "");
      setActiveDragOverFolderKey(null);
      setPressSuppressedBlockId(activeData.blockId ?? null);
    },
    [isBlockDragDisabled],
  );

  const handleBlockDragOver = useCallback((event) => {
    const targetFolderKey = getFolderTargetFromOver(event?.over?.data?.current);
    setActiveDragOverFolderKey(targetFolderKey);
  }, []);

  const handleBlockDragCancel = useCallback(() => {
    clearDragState();
    schedulePressSuppressionClear();
  }, [clearDragState, schedulePressSuppressionClear]);

  const handleBlockDragEnd = useCallback(
    async (event) => {
      const activeData = event?.active?.data?.current;
      const targetFolderKey = getFolderTargetFromOver(event?.over?.data?.current);

      clearDragState();
      schedulePressSuppressionClear();

      if (!activeData || activeData.type !== "blockCard") return;
      if (targetFolderKey == null) return;

      const sourceFolderKey = activeData.sourceFolderKey ?? "";
      if (!isCrossFolderMove(sourceFolderKey, targetFolderKey)) {
        return;
      }

      const blockId = activeData.blockId;
      if (!blockId || !client?.id) return;

      const block = Array.isArray(client?.blocks)
        ? client.blocks.find((entry) => String(entry?.id) === String(blockId))
        : null;
      if (!block) return;

      const targetSection =
        folderSections.find((section) => section.folderKey === targetFolderKey) ??
        null;
      if (!targetSection) return;

      const targetLabel = getPersistedFolderLabelForSection(targetSection);
      const writePlan = buildBlockFolderMoveWritePlan({
        client,
        blockId: block.id,
        targetLabel,
      });

      if (!writePlan) return;

      const rollback = applyOptimisticBlockLabel({
        block,
        client,
        nextLabel: writePlan.label,
      });

      try {
        const timestamp = NetworkManager.getInstance().getNetworkTime();
        const sessionId = client?.coach?.sessionId;

        await updateDoc(
          doc(db, ...writePlan.blockDocPathSegments),
          buildBlockRealtimeWritePayload({
            label: writePlan.label,
            sessionId,
            timestamp,
          }),
        );

        showToast("blockMovedFolderTitle-string", {
          type: "success",
          title: "blockMovedFolderTitle-string",
          description: "blockMovedFolderDescription-string",
          variables: {
            blockName: block?.name ?? "",
            folderName: targetSection.folderLabel,
          },
        });
      } catch (error) {
        rollback();
        showToast("blockMoveFolderErrorTitle-string", {
          type: "error",
          title: "blockMoveFolderErrorTitle-string",
          description: "blockMoveFolderErrorDescription-string",
        });
      }
    },
    [
      clearDragState,
      client,
      folderSections,
      schedulePressSuppressionClear,
      showToast,
    ],
  );

  const hasBlocks = (client?.blocks?.length ?? 0) > 0;
  const hasVisibleFolders = folderSections.length > 0;

  return (
    <View style={styles.container}>
      <View style={styles.contentSurface}>
        <BrowserToolbar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onOpenAddBlock={() => {
            setPreselectedFolderLabel("");
            setShowingAddBlock(true);
          }}
        />

        {client?.isOffline && (
          <Text style={styles.offlineNoticeText}>
            {t("offlineBlocksInformationMissing-string")}
          </Text>
        )}

        {!hasBlocks && (
          <IconEmptyView
            iconLibrary="Ionicons"
            mainText="withoutBlocks-string"
            subText="withoutBlocksText-string"
            iconName="reader-outline"
            marginTop={100}
          />
        )}

        {hasBlocks && !hasVisibleFolders && (
          <IconEmptyView
            iconLibrary="Ionicons"
            iconName="search-outline"
            mainText="noMatchingBlocks-string"
            subText="noMatchingBlocksSubtitle-string"
            marginTop={60}
          />
        )}

        {hasBlocks && hasVisibleFolders && (
          <DndContext
            sensors={sensors}
            onDragStart={handleBlockDragStart}
            onDragOver={handleBlockDragOver}
            onDragEnd={handleBlockDragEnd}
            onDragCancel={handleBlockDragCancel}
          >
            <View style={styles.foldersContainer}>
              {folderSections.map((section) => (
                <FolderSection
                  key={section.folderKey}
                  section={section}
                  isExpanded={expandedFolders[section.folderKey] !== false}
                  onToggleExpand={handleToggleFolder}
                  onEditFolderName={handleEditFolderName}
                  isEditingName={editingFolderKey === section.folderKey}
                  editingDraft={editingFolderLabelDraft}
                  onEditingDraftChange={setEditingFolderLabelDraft}
                  onSaveFolderName={handleSaveFolderName}
                  onCancelEditFolderName={handleCancelFolderNameEdit}
                  isSavingEdit={isSavingFolderName}
                  onCreateBlockInFolder={handleOpenAddBlockFromFolder}
                  screenSize={screenSize}
                  i18n={i18n}
                  t={t}
                  menuItems={blockMenuItems}
                  onPressBlock={handleOpenBlock}
                  onSummaryPress={handleSummaryPress}
                  onMenuAction={handleMenuAction}
                  showRightIcons={showRightIcons}
                  activeBlockId={activeBlockId}
                  blockDragDisabled={isBlockDragDisabled}
                  activeDragBlockId={activeDragBlockId}
                  activeDragSourceFolderKey={activeDragSourceFolderKey}
                  activeDragOverFolderKey={activeDragOverFolderKey}
                  pressSuppressedBlockId={pressSuppressedBlockId}
                />
              ))}
            </View>
          </DndContext>
        )}
      </View>

      {showingAddBlock && (
        <AddBlockView
          isVisible={showingAddBlock}
          onClose={handleCloseAddBlock}
          client={client}
          preselectedFolderLabel={preselectedFolderLabel}
        />
      )}

      {showingCopyToClient && actionBlock && (
        <CopyBlockToClient
          isVisible={showingCopyToClient}
          onClose={handleCloseCopyToClient}
          block={actionBlock}
        />
      )}

      {showingDeleteAlert && actionBlock && (
        <DeleteBlockView
          isVisible={showingDeleteAlert}
          onClose={handleCloseDeleteAlert}
          title="deleteBlockAlertTitle-string"
          text="deleteBlockAlertText-string"
          action={handleDeleteBlock}
          nameOfBlock={actionBlock?.name ?? ""}
        />
      )}

      <BlockSummaryModal
        isVisible={isSummaryModalVisible}
        onClose={handleCloseSummaryModal}
        initialBlockId={summaryBlockId}
        blocks={client?.blocks ?? []}
        screenSize={screenSize}
        activeBlockId={activeBlockId}
      />
    </View>
  );
});

export default BlocksList;
